import { SubmitHandler, useForm } from "react-hook-form";
import Button from "../Button";
import styles from "./SignUpBlock.module.scss";
import classNames from "classnames/bind";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { ResolvedSignUpBlock } from "models/signupBlockResolver";
import RichText from "../RichText";
import TextInput from "../TextInput";
import { useEffect, useRef, useState } from "react";
import DefaultErrorView from "../TextInput/DefaultErrorView";
import UIIcon from "../UIIcon";
import formSchema from "./schema";
import useFormSubmission from "./useFormSubmission";
import Checkbox from "../Checkbox";

const cx = classNames.bind(styles);

export type customerDetailsFormFields = z.infer<typeof formSchema>;

const signUpBlock = (signUpFormProps: ResolvedSignUpBlock) => {
    const recaptchaTokenRef = useRef<HTMLInputElement>(null);
    const formContainerRef = useRef<HTMLDivElement>(null);
    const formErrorMessage =
        "Sorry, we were unable to complete your request this time, please try again later";

    const {
        header,
        responseCopy,
        signUpCopy,
        marketingConsent,
        bannerImagery,
        resubmitText,
        responseImage,
        subCopy,
        responseSubCopy,
        contactSourceReason,
        emailTemplateName,
    } = signUpFormProps;

    const {
        handleSubmit,
        register,
        setError,
        formState: { errors, isSubmitting, isSubmitSuccessful },
        watch,
        reset,
    } = useForm<customerDetailsFormFields>({ resolver: zodResolver(formSchema) });

    const [isFormVisible, setFormVisible] = useState(true);
    const { isFormSubmissionSuccessful, handleFormSubmission, recaptchaToken } =
        useFormSubmission();

    const onSubmit: SubmitHandler<customerDetailsFormFields> = async (data) => {
        try {
            await handleFormSubmission(data, emailTemplateName);
            if (!isFormSubmissionSuccessful.current) {
                setError("root", {
                    message: formErrorMessage,
                });
            }
        } catch (error) {
            setError("root", {
                message: formErrorMessage,
            });
        }
    };

    useEffect(() => {
        const loadRecaptchaScript = async () => {
            const script = document.createElement("script");
            script.src = `https://www.google.com/recaptcha/enterprise.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}`;
            script.async = true;
            script.defer = true;

            document.head.appendChild(script);

            return () => {
                document.head.removeChild(script);
            };
        };

        loadRecaptchaScript();
    }, []);

    useEffect(() => {
        if (isSubmitSuccessful) {
            setFormVisible(false);
            reset();
            if (formContainerRef.current) {
                formContainerRef.current.scrollIntoView({ behavior: "smooth" });
            }
        }
    }, [isSubmitSuccessful, reset]);

    useEffect(() => {
        if (recaptchaToken && recaptchaTokenRef.current) {
            recaptchaTokenRef.current.value = recaptchaToken;
        }
    }, [recaptchaToken]);

    return (
        <div className={cx("container", { isFormVisible })}>
            <div ref={formContainerRef} className={cx("formContainer")}>
                {!isFormVisible && (
                    <UIIcon className={cx("successIcon")} icon="TickOrangeEncircled" />
                )}
                <h2 className={cx("heading")}>{isFormVisible ? header : responseCopy}</h2>
                {isFormVisible ? (
                    <>
                        <RichText className={cx("subCopy")} html={subCopy.html} />
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className={cx("inputContainer")}>
                                <TextInput
                                    {...register("firstName")}
                                    type="text"
                                    className={cx("inputField")}
                                    label="First name"
                                    value={watch("firstName")}
                                    hasError={!!errors.firstName?.type}
                                    errorText={errors.firstName?.message}
                                    errorTextClassName={cx("errorText")}
                                    errorIconClassName={cx("errorIcon")}
                                />
                            </div>
                            <div className={cx("inputContainer")}>
                                <TextInput
                                    {...register("lastName")}
                                    type="text"
                                    className={cx("inputField")}
                                    label="Last name"
                                    value={watch("lastName")}
                                    hasError={!!errors.lastName?.type}
                                    errorText={errors.lastName?.message}
                                    errorTextClassName={cx("errorText")}
                                    errorIconClassName={cx("errorIcon")}
                                />
                            </div>
                            <div className={cx("inputContainer")}>
                                <TextInput
                                    {...register("email")}
                                    type="email"
                                    className={cx("inputField")}
                                    label="Email address"
                                    value={watch("email")}
                                    hasError={!!errors.email?.type}
                                    errorText={errors.email?.message}
                                    errorTextClassName={cx("errorText")}
                                    errorIconClassName={cx("errorIcon")}
                                />
                            </div>
                            <div className={cx("inputContainer")}>
                                <TextInput
                                    {...register("contactNumber")}
                                    type="number"
                                    label="Phone number"
                                    className={cx("inputField")}
                                    pattern="\d+"
                                    value={watch("contactNumber")}
                                    hasError={!!errors.contactNumber?.type}
                                    errorText={errors.contactNumber?.message}
                                    errorTextClassName={cx("errorText")}
                                    errorIconClassName={cx("errorIcon")}
                                />
                            </div>
                            <div className={cx("inputContainer")}>
                                <TextInput
                                    {...register("postCode")}
                                    type="text"
                                    className={cx("inputField")}
                                    label="Postcode (optional)"
                                    value={watch("postCode")}
                                    hasError={!!errors.postCode?.type}
                                    errorText={errors.postCode?.message}
                                    errorTextClassName={cx("errorText")}
                                    errorIconClassName={cx("errorIcon")}
                                />
                            </div>
                            <div className={cx("marketingContainer")}>
                                <Checkbox {...register("marketingConsent")} />
                                <RichText
                                    className={cx("text")}
                                    html={marketingConsent.primaryText?.html}
                                />
                            </div>

                            {!!errors.root && (
                                <DefaultErrorView
                                    errorClassName={cx("globalError")}
                                    errorTextClassName={cx("errorText")}
                                    errorIconClassName={cx("errorIcon")}
                                    errorText={errors.root.message}
                                />
                            )}
                            <input ref={recaptchaTokenRef} name="recaptchaToken" type="hidden" />
                            <input
                                type="hidden"
                                value={contactSourceReason}
                                {...register("contactSourceReason")}
                            />
                            <Button
                                className={cx("button")}
                                type="submit"
                                isDisabled={isSubmitting}
                            >
                                Submit
                            </Button>
                            <p className={cx("signupCopy")}>{signUpCopy}</p>
                        </form>
                    </>
                ) : (
                    <>
                        <RichText
                            className={cx("subCopyResponse")}
                            html={responseSubCopy.html}
                        ></RichText>
                        {!!resubmitText && (
                            <a className={cx("resubmitLink")} onClick={() => setFormVisible(true)}>
                                <span>{resubmitText}</span>
                            </a>
                        )}
                    </>
                )}
            </div>
            <div className={cx("bannerImageryContainer", { isFormVisible })}>
                <img
                    className={cx("image")}
                    src={isFormVisible ? bannerImagery?.src : responseImage.src}
                    alt={isFormVisible ? bannerImagery?.alt : responseImage.src}
                />
            </div>
        </div>
    );
};

export default signUpBlock;
